/**
 * Authentication service
 * Centralized authentication logic with HTTP-only cookies and TTL tracking
 */
import {
  AUTH_ENDPOINTS,
  TTL_REFRESH_THRESHOLD,
  REFRESH_CHECK_INTERVAL,
  LAST_REFRESH_CHECK_KEY,
} from '../constants/auth.constants';
import type {
  LoginRequest,
  LoginResponse,
  LogoutResponse,
  RefreshResponse,
  ProfileResponse,
} from '../types/auth.types';
import { createAuthError, handleFetchError } from '../utils/error.utils';
import { getTTL, getRemainingTTL, isTTLExpired, removeTTL, setTTL } from '../utils/storage.utils';
import { env } from '@/config/env';

// Store the interval ID globally to prevent multiple instances
let refreshIntervalId: NodeJS.Timeout | null = null;

// Store whether the scheduler is paused
let isSchedulerPaused = false;

// Store callbacks to be called after successful token refresh
let onRefreshCallbacks: Array<() => void | Promise<void>> = [];

// Store callbacks to be called after logout
let onLogoutCallbacks: Array<() => void | Promise<void>> = [];

/**
 * Set last refresh check timestamp in localStorage
 */
const setLastRefreshCheck = (timestamp: number): void => {
  localStorage.setItem(LAST_REFRESH_CHECK_KEY, timestamp.toString());
};

/**
 * Execute all registered refresh callbacks
 */
const executeRefreshCallbacks = async (): Promise<void> => {
  await Promise.all(onRefreshCallbacks.map(callback => Promise.resolve(callback())));
};

/**
 * Execute all registered logout callbacks
 */
const executeLogoutCallbacks = async (): Promise<void> => {
  await Promise.all(onLogoutCallbacks.map(callback => Promise.resolve(callback())));
};

/**
 * Scheduled token refresh checker
 * Checks every 5 minutes if refresh is needed
 * Stores last check time in localStorage
 * Triggers refresh when TTL has 10 or fewer minutes remaining
 * Automatically pauses when user is not authenticated
 *
 * @returns interval ID that can be used to clear the interval
 */
export const startScheduledRefreshCheck = (): NodeJS.Timeout => {
  // Prevent multiple intervals from running
  if (refreshIntervalId) {
    return refreshIntervalId;
  }

  const checkAndRefresh = async () => {
    const now = Date.now();

    // Store current check time
    setLastRefreshCheck(now);

    // Check if user is authenticated
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    if (!isAuthenticated()) {
      // Pause scheduler if user is not authenticated
      if (!isSchedulerPaused) {
        isSchedulerPaused = true;
        // eslint-disable-next-line no-console
        console.log('Token refresh scheduler paused - user not authenticated');
      }
      return;
    }

    // Resume scheduler if it was paused
    if (isSchedulerPaused) {
      isSchedulerPaused = false;
      // eslint-disable-next-line no-console
      console.log('Token refresh scheduler resumed');
    }

    // Check if TTL is within threshold (10 minutes or less)
    const remainingTTL = getRemainingTTL();
    if (remainingTTL <= TTL_REFRESH_THRESHOLD && remainingTTL > 0) {
      try {
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        const result = await refreshToken();
        if (result) {
          // eslint-disable-next-line no-console
          console.log('Token refreshed successfully');

          // Execute all registered callbacks (e.g., refresh profile)
          await executeRefreshCallbacks();
        }
      } catch (error_) {
        // eslint-disable-next-line no-console
        console.error('Scheduled refresh failed:', error_);
      }
    }
  };

  // Run initial check immediately
  checkAndRefresh();

  // Set up interval for checks every 5 minutes
  refreshIntervalId = setInterval(checkAndRefresh, REFRESH_CHECK_INTERVAL);

  return refreshIntervalId;
};

/**
 * Stop scheduled refresh checks
 * @param intervalId - Optional interval ID. If not provided, stops the global interval
 */
export const stopScheduledRefreshCheck = (intervalId?: NodeJS.Timeout): void => {
  if (intervalId) {
    clearInterval(intervalId);
    if (intervalId === refreshIntervalId) {
      refreshIntervalId = null;
      isSchedulerPaused = false;
    }
  } else if (refreshIntervalId) {
    clearInterval(refreshIntervalId);
    refreshIntervalId = null;
    isSchedulerPaused = false;
  }
};

/**
 * Resume scheduled refresh checks after login
 * Forces an immediate check and unpauses the scheduler
 */
export const resumeScheduledRefreshCheck = (): void => {
  isSchedulerPaused = false;

  // If scheduler is not running, start it
  if (!refreshIntervalId) {
    startScheduledRefreshCheck();
  }
};

/**
 * Login service
 * Authenticates user and stores TTL in localStorage
 * JWT token is set in HTTP-only cookie by the server
 * Triggers profile fetch after successful login
 * Resumes token refresh scheduler
 */
export const login = async (credentials: LoginRequest): Promise<LoginResponse> => {
  try {
    const response = await fetch(`${env.bffBaseURL}${AUTH_ENDPOINTS.LOGIN}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Timestamp': Date.now().toString(), // Required by BFF for replay attack prevention
      },
      credentials: 'include', // IMPORTANT: Send/receive cookies
      body: JSON.stringify(credentials),
    });

    if (!response.ok) {
      const status = response.status;
      throw createAuthError(status);
    }

    const data: LoginResponse = await response.json();

    // Store TTL in localStorage (only the TTL, not the token!)
    setTTL(data.ttl);

    // Resume token refresh scheduler after login
    resumeScheduledRefreshCheck();

    // Execute all registered callbacks (e.g., fetch profile after login)
    await executeRefreshCallbacks();

    return data;
  } catch (error: unknown) {
    throw handleFetchError(error);
  }
};

/**
 * Logout service
 * Clears all user data and calls logout endpoint
 *
 * What gets cleared:
 * - localStorage: All items (including TTL)
 * - sessionStorage: All items
 * - HTTP-only cookie: Cleared by server on successful logout
 * - Triggers logout callbacks (e.g., clear profile)
 *
 * Returns success status based on 200 response
 */
export const logout = async (): Promise<LogoutResponse> => {
  try {
    const response = await fetch(`${env.bffBaseURL}${AUTH_ENDPOINTS.LOGOUT}`, {
      method: 'POST',
      headers: {
        'X-Timestamp': Date.now().toString(), // Required by BFF for replay attack prevention
      },
      credentials: 'include', // Send cookie to invalidate on server
    });

    // Always clear all local storage and session storage
    // This ensures complete cleanup regardless of server response
    localStorage.clear();
    sessionStorage.clear();

    // Pause the token refresh scheduler
    isSchedulerPaused = true;

    // Execute all registered logout callbacks (e.g., clear profile)
    await executeLogoutCallbacks();

    if (!response.ok) {
      throw createAuthError(response.status, 'Logout failed');
    }

    const data: LogoutResponse = await response.json();

    // Check for successful logout with 200 status
    if (response.status === 200 && data.success) {
      return data;
    }

    throw createAuthError(response.status, data.message || 'Logout failed');
  } catch (error: unknown) {
    // Even if logout fails, we've already cleared local storage
    // This ensures the user is logged out locally
    const authError = handleFetchError(error);

    // Return a logout response indicating failure but local cleanup was done
    return {
      success: false,
      message: authError.message,
    };
  }
};

/**
 * Refresh token service
 * Calls refresh endpoint to get a new authentication cookie
 * Only refreshes if TTL is within threshold (10 minutes or less remaining)
 */
export const refreshToken = async (): Promise<RefreshResponse | null> => {
  try {
    // Check if refresh is needed
    const remainingTTL = getRemainingTTL();

    // If more than 10 minutes remaining, no need to refresh
    if (remainingTTL > TTL_REFRESH_THRESHOLD) {
      return null;
    }

    // If TTL is expired, don't refresh (user needs to login again)
    if (isTTLExpired()) {
      removeTTL();
      throw createAuthError(401, 'Session expired. Please login again.');
    }

    const response = await fetch(`${env.bffBaseURL}${AUTH_ENDPOINTS.REFRESH}`, {
      method: 'POST',
      headers: {
        'X-Timestamp': Date.now().toString(), // Required by BFF for replay attack prevention
      },
      credentials: 'include', // Sends current cookie, receives new one
    });

    if (!response.ok) {
      // If refresh fails, remove TTL
      removeTTL();
      throw createAuthError(response.status, 'Token refresh failed');
    }

    const data: RefreshResponse = await response.json();

    // Check for successful refresh with 200 status
    if (response.status === 200 && data.success && data.ttl) {
      setTTL(data.ttl);
      return data;
    }

    throw createAuthError(response.status, data.message || 'Token refresh failed');
  } catch (error: unknown) {
    throw handleFetchError(error);
  }
};

/**
 * Check if user is authenticated
 * Validates that TTL exists and hasn't expired
 */
export const isAuthenticated = (): boolean => {
  const ttl = getTTL();

  if (!ttl) {
    return false;
  }

  // Check if TTL has expired
  if (isTTLExpired()) {
    removeTTL();
    return false;
  }

  return true;
};

/**
 * Auto-refresh token if TTL is within threshold
 * This should be called periodically or on app mount
 */
export const autoRefreshToken = async (): Promise<void> => {
  if (!isAuthenticated()) {
    return;
  }

  try {
    await refreshToken();
  } catch (error_) {
    // Silently fail - user will need to login again
    // eslint-disable-next-line no-console
    console.error('Auto-refresh failed:', error_);
  }
};

/**
 * Get last refresh check timestamp from localStorage
 * @returns timestamp in milliseconds or null if never checked
 */
export const getLastRefreshCheck = (): number | null => {
  const lastCheck = localStorage.getItem(LAST_REFRESH_CHECK_KEY);
  return lastCheck ? Number.parseInt(lastCheck, 10) : null;
};

/**
 * Register a callback to be called after successful token refresh
 * @param callback - Function to call after token refresh
 * @returns Unregister function
 */
export const onTokenRefresh = (callback: () => void | Promise<void>): (() => void) => {
  onRefreshCallbacks.push(callback);

  // Return unregister function
  return () => {
    onRefreshCallbacks = onRefreshCallbacks.filter(cb => cb !== callback);
  };
};

/**
 * Register a callback to be called after logout
 * @param callback - Function to call after logout
 * @returns Unregister function
 */
export const onLogout = (callback: () => void | Promise<void>): (() => void) => {
  onLogoutCallbacks.push(callback);

  // Return unregister function
  return () => {
    onLogoutCallbacks = onLogoutCallbacks.filter(cb => cb !== callback);
  };
};

/**
 * Initialize authentication system
 * Call this once at application startup to begin automatic token refresh
 * Safe to call multiple times - will not create duplicate intervals
 *
 * @example
 * // In your main App component or index file:
 * import { initializeAuth } from '@/Auth';
 * initializeAuth();
 */
export const initializeAuth = (): void => {
  startScheduledRefreshCheck();
};

/**
 * Get user profile
 * Fetches the authenticated user's profile information
 * Requires valid authentication cookie
 * Uses authenticatedFetch to automatically handle 401 errors
 *
 * @returns ProfileResponse on 200
 * @throws AuthError on 401 (invalid/missing cookie) or 500 (internal server error)
 */
export const getProfile = async (): Promise<ProfileResponse> => {
  try {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    const response = await authenticatedFetch(`${env.bffBaseURL}${AUTH_ENDPOINTS.PROFILE}`, {
      method: 'GET',
    });

    if (!response.ok) {
      const status = response.status;
      throw createAuthError(status, 'Failed to fetch profile');
    }

    const data: ProfileResponse = await response.json();

    return data;
  } catch (error: unknown) {
    throw handleFetchError(error);
  }
};

/**
 * Make authenticated request with automatic 401 handling
 * Helper function to make requests with credentials
 * Automatically logs out user and redirects to login on 401 responses
 */
export const authenticatedFetch = async (
  url: string,
  options: RequestInit = {},
): Promise<Response> => {
  // Merge headers properly
  const mergedOptions: RequestInit = {
    ...options,
    credentials: 'include', // Always include cookies
    headers: {
      'X-Timestamp': Date.now().toString(), // Required by BFF for replay attack prevention
      ...options.headers,
    },
  };

  const response = await fetch(url, mergedOptions);

  // Handle 401 Unauthorized - Auto logout
  if (response.status === 401) {
    // eslint-disable-next-line no-console
    console.warn('401 Unauthorized - Logging out user automatically');

    // Clear all storage and invalidate session
    localStorage.clear();
    sessionStorage.clear();

    // Pause the token refresh scheduler
    isSchedulerPaused = true;

    // Execute all registered logout callbacks (e.g., clear profile)
    await executeLogoutCallbacks();

    // Force immediate redirect to login page with page reload
    // Using replace() instead of href assignment to avoid history entry
    // This will immediately navigate away and stop all JavaScript execution
    globalThis.location.replace('/');

    // The code below will not execute due to the redirect above
    // but we keep it for TypeScript/ESLint satisfaction
    // Return a never-resolving promise to prevent further processing
    return new Promise<Response>(() => {
      // This promise never resolves, ensuring no code after authenticatedFetch runs
    }) as never;
  }

  return response;
};
